import React, { useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { appAxios } from "../../axios/axios";
import { toast } from "react-toastify";
import { SelectWrapper } from "../Common/Ui/SelectWrapper";
import { CustomInput } from "../Common/Ui/CustomInput";
import { CustomStaticSelect } from "../Common/Ui/CustomStaticSelect";
import { useForm } from "react-hook-form";
import { joiResolver } from "@hookform/resolvers/joi";
import Joi from "joi";
import { useAppSelector } from "../../store";
import moment from "moment";
import ReCaptcha from "../Common/Ui/ReCaptcha";
import { ErrorText } from "../errors/ErrorText";
import { LoadingButton } from "../Common/Ui/LoadingButton";
import { ICallbackRequest } from "../../interfaces/enquiry.interface";
import { IUserData } from "../../interfaces/userData.interface";
import { SearchCustomerSelect } from "../Common/SearchCustomerSelect";
import { getDepartmentList } from "../../utils/Department";
import { IDepartment } from "../../interfaces/department.interface";
import { LoadingOverlaySelect } from "../Common/Ui/LoadingOverlaySelect";
import { get } from "js-cookie";
import { error } from "console";
import { getSiteStaffList } from "../../utils/User";
const CustomCkEditor = React.lazy(() => import("../Common/Ui/CustomCkEditor"));

interface ISelectedCustomer {
  label: string;
  value: string;
  name: string;
  phone: string;
}

const callBackRequestSchema = Joi.object<ICallbackRequest>({
  customerUUID: Joi.string().optional().allow("").allow(null),
  customerName: Joi.string().trim().required().label("Customer Name"),
  customerNumber: Joi.string().trim().required().label("Customer Number"),
  enquiryId: Joi.number().optional().allow(null),
  createdByUserUUID: Joi.string().optional().allow(""),
  requestForStaffUUID: Joi.string().optional().label("Staff").allow(null),
  callTime: Joi.date().required().label("Call Time"),
  callbackRequestRemark: Joi.string().trim().required().label("Remark"),

  departmentId: Joi.number()
    .required()

    .label("Department")
    .messages({
      "number.base": "Please select department",
    }),
});

export const CallbackRequestModal: React.FC<{
  show: boolean;
  onClose: () => void;
  // siteStaff: IUserData[];
  onSave: () => void;
  enquiryId?: number;
}> = (props) => {
  const userData = useAppSelector((store) => store.users.userData);
  const [selectedCustomer, setSelectedCustomer] =
    useState<ISelectedCustomer | null>();
  const [sendingRequest, setSendingRequest] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | undefined>(
    undefined
  );
  const reCaptchaRef = useRef<any>();
  const defaultValues: ICallbackRequest = {
    customerUUID: null,
    customerName: "",
    customerNumber: "",
    enquiryId: null,
    createdByUserUUID: userData?.uuid,
    departmentId: null,
    requestForStaffUUID: null,
    callTime: moment(new Date(), "YYYY-MM-DD[T]HH:mm").toDate(),
    callbackRequestRemark: "",
  };
  const {
    handleSubmit,
    register,
    reset,
    watch,
    formState,
    setValue,
    getValues,
  } = useForm<ICallbackRequest>({
    resolver: joiResolver(callBackRequestSchema),
    defaultValues,
  });

  const [allSiteStaff, setAllSiteStaff] = useState<IUserData[]>([]);

  const getSiteStaff = async () => {
    const staffResponse = await getSiteStaffList();
    setAllSiteStaff(staffResponse.result);
  };

  const siteStaffOptArr = allSiteStaff.map((staff) => {
    return {
      label: `${staff.firstName} ${staff.lastName}  (${staff.email})`,
      value: staff.uuid,
    };
  });
  // siteStaffOptArr.unshift({
  //   label: "Select Staff",
  //   value: "",
  // });

  const [departmentList, setDepartmentList] = useState<IDepartment[]>([]);

  const setDepartmentListInState = async () => {
    const departmentList = await getDepartmentList();
    if (departmentList.success) {
      setDepartmentList(departmentList.result);
    } else {
      console.log(departmentList.errorMessage);
    }
  };

  const getStaffOptionsBasedOnDepartment = () => {
    const departmentId = getValues("departmentId");
    const department = departmentList.find(
      (department) => department.id === departmentId
    );
    if (department) {
      const staffList = department.users;
      const staffOptArr = staffList.map((staff) => {
        return {
          label: `${staff.firstName} ${staff.lastName}  (${staff.email})`,
          value: staff.uuid,
        };
      });
      staffOptArr.unshift({
        label: "Select Staff",
        value: "",
      });
      return staffOptArr;
    } else {
      return [];
    }
  };

  useEffect(() => {
    setDepartmentListInState();
    getSiteStaff();
  }, []);

  async function submitHandler(data: ICallbackRequest) {
    let token: string | undefined = undefined;
    if (!captchaToken) {
      token = await reCaptchaRef.current.executeAsync();
      setCaptchaToken(token);
    }
    if (!captchaToken && !token) {
      return toast.error("Please solve captcha");
    }
    setSendingRequest(true);
    try {
      const saveResponse = await appAxios(
        "/query-management-staff/saveCallBackRequestEnquiry",
        {
          method: "POST",
          headers: {
            "captcha-token": captchaToken ? captchaToken : token,
          },
          data: {
            enquiryData: {
              ...data,
              callTime: moment(data.callTime).utc(),
            },
          },
        }
      );
      if (saveResponse.data.success) {
        toast.success("Request created successfully");
        reset(defaultValues);
        setSelectedCustomer(undefined);
        props.onSave();
      }
    } catch (error) {
      toast.error("Request failed");
    }
    setSendingRequest(false);
  }

  const callTime = watch("callTime");
  const departmentId = watch("departmentId");
  const requestForStaffUUID = watch("requestForStaffUUID");

  return (
    <Modal show={props.show} size="xl" centered onHide={props.onClose}>
      <Modal.Header closeButton>
        <h4>Create Call Request</h4>
      </Modal.Header>
      <Modal.Body>
        <div className="container">
          <div className="row">
            <div className="col-lg-3">
              <label htmlFor="">Select Customer</label>
              <SearchCustomerSelect
                value={selectedCustomer?.value}
                onChange={(e) => {
                  setSelectedCustomer(e);
                  setValue("customerName", e?.name || "");
                  setValue("customerNumber", e?.phone || "");
                  setValue("customerUUID", e?.value || null);
                }}
              />
            </div>
            <div className="col-lg-3">
              <CustomInput
                id="customer_name"
                label="Customer Name"
                register={register}
                name="customerName"
                required={true}
                readOnly={selectedCustomer ? true : false}
              />
              <ErrorText
                className="text-danger"
                error={formState.errors.customerName?.message}
              />
            </div>
            <div className="col-lg-3">
              <CustomInput
                id="customer_Number"
                label="Customer Number"
                register={register}
                name="customerNumber"
                required={true}
                readOnly={selectedCustomer ? true : false}
              />
              <ErrorText
                className="text-danger"
                error={formState.errors.customerNumber?.message}
              />
            </div>

            <div className="col-lg-3">
              <CustomInput
                id="call_time"
                label="Call time"
                type="datetime-local"
                value={moment(callTime).format("YYYY-MM-DD[T]HH:mm")}
                onChange={(e) => {
                  setValue(
                    "callTime",
                    moment(e.target.value, "YYYY-MM-DD[T]hh:mm").toDate()
                  );
                }}
                min={moment().format("YYYY-MM-DD[T]hh:mm")}
                required
              />
            </div>

            {/* The logic behind selecting these options for department and staff are:
1. If the user selects a department first, then he will get an option of selecting only those staff members who are in that particular department.
2. If the user selects staff first, then the primary department of the staff will be selected automatically. */}

            <div className="col-lg-3">
              <label htmlFor="">Select Department</label>
              <SelectWrapper
                options={departmentList.map((department) => {
                  return {
                    label: department.departmentName,
                    value: department.id?.toString() || "",
                  };
                })}
                onChange={(e) => {
                  let value: number | null = null;
                  if (e?.value) {
                    value = parseInt(e.value);
                  }
                  setValue("departmentId", value);
                }}
                value={departmentId ? departmentId.toString() : ""}
                isLoading={false}
              />
              <ErrorText
                className="text-danger"
                error={formState.errors.departmentId?.message}
              />
            </div>

            <div className="col-lg-3 text-dark">
              <label htmlFor="">Select Staff</label>

              <SelectWrapper
                options={
                  departmentId
                    ? getStaffOptionsBasedOnDepartment()
                    : siteStaffOptArr
                }
                onChange={(e) => {
                  setValue("requestForStaffUUID", e?.value || null);
                  let primaryDepartmentId: number | null = null;

                  departmentList.forEach((dep) => {
                    const isExist = dep.crmDepartmentStaffUserMapping.find(
                      (staffMapping) =>
                        staffMapping.staffUserUUID === e?.value &&
                        staffMapping.isPrimary
                    );
                    if (isExist && dep.id) {
                      primaryDepartmentId = dep.id;
                    }
                  });

                  if (!departmentId) {
                    setValue("departmentId", primaryDepartmentId);
                  }
                }}
                value={requestForStaffUUID ? requestForStaffUUID : ""}
                name="requestForStaffUUID"
                placeholder="Select staff"
                // required
              />
              <ErrorText
                className="text-danger"
                error={formState.errors.requestForStaffUUID?.message}
              />

              {/* <label htmlFor="">Select Staff</label>
              <SelectWrapper
                options={getStaffOptions()}
                onChange={(e) => {
                  setValue("requestForStaffUUID", e?.value || "");
                }}
                value={requestForStaffUUID ? requestForStaffUUID : ""}
                isLoading={false}
              /> */}
            </div>
          </div>
          <div className="my-3 col-lg-6 col-12">
            <label className="field-required">Remark</label>
            <CustomCkEditor
              data={getValues("callbackRequestRemark")}
              onChange={(data) => {
                setValue("callbackRequestRemark", data);
              }}
            />
            <ErrorText
              className="text-danger"
              error={formState.errors.callbackRequestRemark?.message}
            />
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <button
          className="btn btn-secondary py-1 rounded-0"
          type="button"
          onClick={() => {
            props.onClose();
          }}
        >
          Close
        </button>
        <LoadingButton
          loading={sendingRequest}
          minWidth="123px"
          btnType="submit"
          onClick={handleSubmit(submitHandler)}
        >
          Create Request
        </LoadingButton>
      </Modal.Footer>
      <ReCaptcha
        setToken={(token: string) => {
          setCaptchaToken(token);
        }}
        myRef={reCaptchaRef}
      />
    </Modal>
  );
};
